import { useState, useCallback, useEffect, useMemo } from "react";
import produce from "immer";
import { useSMCSnackbar } from "../../../utils/hooks";
import { useTranslation } from "react-i18next";
import { setProperty, getErrorCode } from "../../../utils/utils";
import Api from "../../../api/SMPFetchHelper";
import { ObjectTypes, ApiErrorCodes, ErrorCodes } from "../../../api/consts";
import { useSMCErrorHandler } from "../../../utils/errorUtils";
import { PortalSettingsGetSchema } from "../../../api/DBSchema/PortalSettings";
import PortalSettingsGeneral from "./InnerPages/PortalSettingsGeneral";
import generalIcon from "../../../assets/images/general.png";
import clipboardIcon from "../../../assets/images/clipboard.png";
import dnsIcon from "../../../assets/images/dns.png";
import pageIcon from "../../../assets/images/page.png";
import mailIcon from "../../../assets/images/mail.png";
import uploadIcon from "../../../assets/images/upload.png";
import encryptionIcon from "../../../assets/images/encryption.png";
import megaphoneIcon from "../../../assets/images/megaphone.png";
import alertsIcon from "../../../assets/images/alerts.png";
import padlockIcon from "../../../assets/images/padlock.png";
import radiusIcon from "../../../assets/images/action_authorize.png";
import reportsIcon from "../../../assets/images/reports.png";
import userPasswordSecurityIcon from "../../../assets/images/lock_passcode.png";
import PortalSettingsLogging from "./InnerPages/PortalSettingsLogging";
import PortalSettingsDns from "./InnerPages/PortalSettingsDns";
import PortalSettingsCustomFields from "./InnerPages/PortalSettingsCustomFields";
import PortalSettingsMail from "./InnerPages/PortalSettingsMail";
import PortalSettingsFirmware from "./InnerPages/PortalSettingsFirmware";
import PortalSettingsCertificate from "./InnerPages/PortalSettingsCertificates";
import PortalSettingsGatewayNotifications from "./InnerPages/PortalSettingsGatewayNotifications";
import PortalSettingsGatewayReportsNotifications from "./InnerPages/PortalSettingsGatewayReportsNotifications";
import PortalSettingsUserNotifications from "./InnerPages/PortalSettingsUserNotifications";
import PortalSettingsSummaryMessagesNotifications from "./InnerPages/PortalSettingsSummaryMessagesNotifications";
import PortalSettingsReportsNotifications from "./InnerPages/PortalSettingsReportsNotifications";
import PortalSettingsCustomAlerts from "./InnerPages/PortalSettingsCustomAlerts";
import PortalSettingsTwoStepAuthentication from "./InnerPages/PortalSettingsTwoStepAuthentication";
import PortalSettingsRadiusAuth from "./InnerPages/PortalSettingsRadiusAuth";
import PortalSettingsUserPasswordSecurity from "./InnerPages/PortalSettingsUserPasswordSecurity";
import PortalSettingsCyberReports from "./InnerPages/PortalSettingsCyberReports";
import { helpContextPageUrl } from "../../../conf/Routes";
export function usePortalSettingsEditor(defaultChosenPageId) {
  const { t } = useTranslation();
  const {
    enqueueErrorSnackbar,
    enqueueLogoutSnackbar,
    enqueueSuccessSnackbar,
  } = useSMCSnackbar();
  const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

  const [origPortalSettingsObject, setOrigPortalSettingsObject] = useState({});
  const [portalSettingsObject, setPortalSettingsObject] = useState({});

  const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
  const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
  const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
  const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

  const [isObjectDirty, setIsObjectDirty] = useState(false);

  const [chosenPageId, setChosenPageId] = useState(
    defaultChosenPageId || "general"
  );

  const editorPages = useMemo(
    () => [
      {
        id: PortalSettingsGetSchema.pages.general.pageId,
        title: t("SMP.GENERAL"),
        icon: generalIcon,
        pageElement: PortalSettingsGeneral,
        helpId: helpContextPageUrl.domainSettingsGeneral,
      },
      {
        id: PortalSettingsGetSchema.pages.logging.pageId,
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.LOGGING"),
        icon: clipboardIcon,
        pageElement: PortalSettingsLogging,
        helpId: helpContextPageUrl.domainSettingsLogging,
      },
      {
        id: PortalSettingsGetSchema.pages.dns.pageId,
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.DNS"),
        icon: dnsIcon,
        pageElement: PortalSettingsDns,
        helpId: helpContextPageUrl.domainSettingsDNS,
      },
      {
        id: PortalSettingsGetSchema.pages.customFields.pageId,
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.CUSTOM_FIELDS"),
        icon: pageIcon,
        pageElement: PortalSettingsCustomFields,
        helpId: helpContextPageUrl.domainSettingsCustomFields,
      },
      {
        id: PortalSettingsGetSchema.pages.mail.pageId,
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.MAIL"),
        icon: mailIcon,
        pageElement: PortalSettingsMail,
        helpId: helpContextPageUrl.domainSettingsMail,
      },
      {
        id: PortalSettingsGetSchema.pages.firmware.pageId,
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.FIRMWARE"),
        icon: uploadIcon,
        pageElement: PortalSettingsFirmware,
        helpId: helpContextPageUrl.domainSettingsFirmware,
      },
      {
        id: PortalSettingsGetSchema.pages.certificates.pageId,
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.CERTIFICATES"),
        icon: encryptionIcon,
        pageElement: PortalSettingsCertificate,
        helpId: helpContextPageUrl.systemSettingsCertificateAuthority,
      },
      {
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.NOTIFICATIONS"),
        icon: megaphoneIcon,
        children: [
          {
            id: PortalSettingsGetSchema.pages.notificationGateways.pageId,
            title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.GATEWAYS"),
            icon: pageIcon,
            pageElement: PortalSettingsGatewayNotifications,
            helpId: helpContextPageUrl.domainSettingsGatewayNotifications,
          },
          {
            id: PortalSettingsGetSchema.pages.notificationGatewaysReports
              .pageId,
            title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.GATWAY_REPORTS"),
            icon: pageIcon,
            pageElement: PortalSettingsGatewayReportsNotifications,
            helpId:
              helpContextPageUrl.domainSettingsGatewayReportsNotifications,
          },
          {
            id: PortalSettingsGetSchema.pages.notificationUsers.pageId,
            title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.USERS"),
            icon: pageIcon,
            pageElement: PortalSettingsUserNotifications,
            helpId: helpContextPageUrl.domainSettingsUserNotifications,
          },
          {
            id: PortalSettingsGetSchema.pages.notificationSummaryMessages
              .pageId,
            title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.SUMMARY_MESSAGES"),
            icon: pageIcon,
            pageElement: PortalSettingsSummaryMessagesNotifications,
            helpId:
              helpContextPageUrl.domainSettingsSummaryMessagesNotifications,
          },
          {
            id: PortalSettingsGetSchema.pages.notificationReports.pageId,
            title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.REPORTS"),
            icon: pageIcon,
            pageElement: PortalSettingsReportsNotifications,
            helpId: helpContextPageUrl.domainSettingsReportsNotifications,
          },
        ],
      },
      {
        id: PortalSettingsGetSchema.pages.customAlerts.pageId,
        title: t("SMP.SERVICE_DOMAIN.SETTINGS.INNER_MENU.CUSTOM_ALERTS"),
        icon: alertsIcon,
        pageElement: PortalSettingsCustomAlerts,
        helpId: helpContextPageUrl.domainSettingsCustomAlerts,
      },
      {
        id: PortalSettingsGetSchema.pages.twoStepAuth.pageId,
        title: t("SMP.TREE_NODE_INNER.TWO_STEP_AUTHENTICATION"),
        icon: padlockIcon,
        pageElement: PortalSettingsTwoStepAuthentication,
        helpId: helpContextPageUrl.settingsTwoStepAuthentication,
      },
      {
        id: PortalSettingsGetSchema.pages.radiusAuth.pageId,
        title: "Radius Auth",
        icon: radiusIcon,
        pageElement: PortalSettingsRadiusAuth,
      },
      {
        id: PortalSettingsGetSchema.pages.userPasswordSecurity.pageId,
        title: "User Password Security",
        icon: userPasswordSecurityIcon,
        pageElement: PortalSettingsUserPasswordSecurity,
      },
      {
        id: PortalSettingsGetSchema.pages.domainCyberReports.pageId,
        title: "Domain Cyber Reports",
        icon: reportsIcon,
        pageElement: PortalSettingsCyberReports,
      },
    ],
    [t]
  );

  const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
    const _pageIdToPageName = {};
    const _pageIdToHelpId = {};
    const addPagesData = (pagesRoot) => {
      pagesRoot.forEach((page) => {
        if (page.id) {
          _pageIdToPageName[page.id] = page.title;
          _pageIdToHelpId[page.id] = page.helpId;
        }
        if (page.children) {
          addPagesData(page.children);
        }
      });
    };
    addPagesData(editorPages);
    return [_pageIdToPageName, _pageIdToHelpId];
  }, [editorPages]);

  const helpId = useMemo(
    () => pageIdToHelpId[chosenPageId],
    [pageIdToHelpId, chosenPageId]
  );

  const fetchPortalSettings = useCallback(async () => {
    setIsPageDataLoaded(false);
    try {
      const fetchPortalSettingsResp = await Api.fetchSingleObject(
        ObjectTypes.PORTAL_SETTINGS
      );
      const portalSettings = fetchPortalSettingsResp.data.data;
      // Validate server response
      if (!portalSettings) {
        console.error(
          "Get portal settings response failed: portal settings response returned empty"
        );
        throw new Error(ErrorCodes.SERVER_ERROR);
      }
      setOrigPortalSettingsObject(portalSettings);
      setPortalSettingsObject(portalSettings);
      setIsPageLoadFailed(false);
      setIsObjectDirty(false); // Just fetched, not dirty
    } catch (e) {
      const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
      if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
        enqueueLogoutSnackbar(t(errorCode));
      } else {
        enqueueErrorSnackbar(t(errorCode));
      }
      setIsPageLoadFailed(true);
    }
    setIsPageDataLoaded(true);
  }, [enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);

  const setPortalSettingsData = useCallback((paramPath, newValue) => {
    setIsObjectDirty(true); // Data changed, object is dirty
    setPortalSettingsObject((portalSettingsObj) =>
      produce(portalSettingsObj, (draft) => {
        setProperty(draft, paramPath, newValue);
      })
    );
  }, []);

  const verifyData = useCallback(() => {
    setIsInnerPageLoading(true);
    Api.verifySingleObject(
      ObjectTypes.PORTAL_SETTINGS,
      undefined,
      portalSettingsObject
    )
      .then(() => {
        // On no error, clear error message
        setErrorMessagesByPageList(null);
      })
      .catch((error) => {
        const errorMessageArrayByPage = getMessagesArrayByPages(
          error,
          pageIdToPageName
        );
        if (errorMessageArrayByPage) {
          setErrorMessagesByPageList(errorMessageArrayByPage);
        }
      })
      .finally(() => {
        setIsInnerPageLoading(false);
      });
  }, [getMessagesArrayByPages, portalSettingsObject, pageIdToPageName]);

  const onPageChange = useCallback(
    (pageId) => {
      verifyData();
      setChosenPageId(pageId);
    },
    [verifyData]
  );

  const reloadSettingsFirmware = useCallback(() => {
    Api.fetchSingleObject(ObjectTypes.PORTAL_SETTINGS).then((resp) => {
      const portalSettings = resp.data.data;
      const firmwarePage =
        portalSettings[PortalSettingsGetSchema.pages.firmware.pageId];
      setPortalSettingsData(
        PortalSettingsGetSchema.pages.firmware.pageId,
        firmwarePage
      );
    });
    // .catch(e => {
    // enqueueErrorSnackbar('Error reloading firmware page');
    // })
  }, [setPortalSettingsData]);

  const saveButtonOnClick = useCallback(() => {
    Api.updateSingleObject(
      ObjectTypes.PORTAL_SETTINGS,
      undefined,
      portalSettingsObject
    )
      .then(() => {
        setIsPageDataLoaded(false);
        setErrorMessagesByPageList(null);
        fetchPortalSettings();
        enqueueSuccessSnackbar(t("OBJECT_EDITOR_OBJ_SAVED_OK"));
      })
      .catch((error) => {
        const errorMessage = getErrorMessage(error);
        alert(errorMessage);
      })
      .finally(() => {
        setIsPageDataLoaded(true);
      });
  }, [
    fetchPortalSettings,
    portalSettingsObject,
    getErrorMessage,
    enqueueSuccessSnackbar,
    t,
  ]);

  const refreshButtonOnClick = useCallback(() => {
    setIsPageDataLoaded(false);
    fetchPortalSettings();
    setIsPageDataLoaded(true);
  }, [fetchPortalSettings]);

  const revertButtonOnClick = useCallback(() => {
    setPortalSettingsObject(origPortalSettingsObject);
    setIsObjectDirty(false); // Is not dirty, object is reset to originl values
    setErrorMessagesByPageList(null);
  }, [origPortalSettingsObject]);

  // Fetch portal settings on loading of page
  useEffect(() => {
    fetchPortalSettings();
  }, [fetchPortalSettings]);

  return {
    portalSettingsObject,
    isObjectDirty,
    editorPages,
    helpId,
    isPageDataLoaded,
    isInnerPageLoading,
    isPageLoadFailed,
    chosenPageId,
    errorMessagesByPageList,
    onPageChange,
    setPortalSettingsData,
    reloadSettingsFirmware,
    saveButtonOnClick,
    refreshButtonOnClick,
    revertButtonOnClick,
    reloadPageData: fetchPortalSettings,
  };
}
